import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import Prose from '../components/Prose';
import Spinner from '../components/Spinner';
import { api, type Page, type SiteSettings } from '../lib/api';

/*
  The intro copy comes from the CMS page, while the phone/email/address block is
  rendered from the `contact` setting — so those details live in exactly one
  place and the footer never drifts out of sync with this page.
*/
const iconClass = 'h-5 w-5 shrink-0 text-gold-600';

const PhoneIcon = () => (
  <svg aria-hidden viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.9">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.5 5.5A2.5 2.5 0 0 1 5 3h1.6a1 1 0 0 1 .96.73l.9 3.1a1 1 0 0 1-.4 1.1l-1.3.9a13.5 13.5 0 0 0 6.4 6.4l.9-1.3a1 1 0 0 1 1.1-.4l3.1.9a1 1 0 0 1 .73.96V19a2.5 2.5 0 0 1-2.5 2.5A16.5 16.5 0 0 1 2.5 5.5Z"
    />
  </svg>
);

const MailIcon = () => (
  <svg aria-hidden viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.9">
    <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="m3.5 7 7.4 5.3a2 2 0 0 0 2.2 0L20.5 7" />
  </svg>
);

const PinIcon = () => (
  <svg aria-hidden viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.9">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z"
    />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);

export default function Contact({ settings }: { settings: SiteSettings }) {
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const contact = settings.contact;

  useEffect(() => {
    let cancelled = false;
    api<{ page: Page }>('/pages/contact-us')
      .then((r) => !cancelled && setPage(r.page))
      .catch(() => !cancelled && setPage(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    document.title = 'Contact Us — FTTMY';
    return () => {
      document.title = 'Full Time Training in Malaysia';
    };
  }, []);

  if (loading) return <Spinner label="Loading" />;

  const cardClass =
    'flex items-start gap-4 rounded-2xl border border-ink-100 bg-linear-to-b from-white to-sand-50/60 p-6 shadow-sm shadow-ink-900/5';

  return (
    <article>
      <PageHeader title={page?.title ?? 'Contact Us'} />

      <div className="mx-auto w-full px-4 pb-14 pt-5 sm:px-6 lg:px-10 lg:pb-16 xl:px-14">
        {page?.contentHtml && <Prose html={page.contentHtml} className="mb-10" />}

        {/*
          Held to a readable width: the page container runs edge to edge, and a
          two-card row stretched across a wide monitor reads as empty space with
          a phone number lost in it.

          Email leads - it is the address the office actually works from. Both
          numbers share one card rather than repeating the Phone label twice;
          each number stays its own tel: link so tapping either still dials.
        */}
        <div className="grid max-w-4xl gap-5 sm:grid-cols-2">
          {contact?.email && (
            <a
              href={`mailto:${contact.email}`}
              className={`${cardClass} transition-colors hover:border-gold-200`}
            >
              <MailIcon />
              <span className="min-w-0">
                <span className="block text-lg font-bold text-gold-700">
                  Email
                </span>
                <span className="mt-1 block truncate text-lg text-ink-900">
                  {contact.email}
                </span>
              </span>
            </a>
          )}

          {contact?.phone && contact.phone.length > 0 && (
            <div className={cardClass}>
              <PhoneIcon />
              <span className="min-w-0">
                <span className="block text-lg font-bold text-gold-700">
                  Phone
                </span>
                <span className="mt-1 flex flex-col items-start gap-0.5">
                  {contact.phone.map((number) => (
                    <a
                      key={number}
                      href={`tel:${number.replace(/[^+\d]/g, '')}`}
                      className="text-lg text-ink-900 transition-colors hover:text-gold-600"
                    >
                      {number}
                    </a>
                  ))}
                </span>
              </span>
            </div>
          )}

          {contact?.address?.trim() && (
            <div className={`${cardClass} sm:col-span-2`}>
              <PinIcon />
              <span>
                <span className="block text-lg font-bold text-gold-700">
                  Address
                </span>
                <span className="mt-1 block whitespace-pre-line text-lg text-ink-900">
                  {contact.address}
                </span>
              </span>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
